import { gql } from '@apollo/client';
import { Box, Grid, HStack, Stat, StatLabel, StatNumber, Text, VStack } from '@chakra-ui/react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import {
  DisplayStatsIcon,
  StatsFailedIcon,
  StatsSyncIcon,
  StatsSyncingIcon,
  StatsWarningIcon,
} from '~components/ui/icons';
import { ConfigurationStatus } from '~graphql/__generated__/types';
import { ConfigurationStatusType } from '../ConfigStateToggle';
import { ConfigSummary_ConfigFragment } from './__generated__/ConfigSummary.graphql';
import { useConfigDetailSettingsTab } from './ConfigDetailView';

type OutletContextType = {
  configStatus: ConfigurationStatusType;
};

export function ConfigSummaryView({ config }: { config: ConfigSummary_ConfigFragment }) {
  const { t } = useTranslation();

  const { configStatus } = useOutletContext<OutletContextType>();

  const showShouldLastPublished = configStatus === ConfigurationStatus.Published;

  const firmware = config.shouldUpdateToLatestFirmware
    ? t('latestFirmwareVersion')
    : (config.firmware?.android?.version && config.firmware?.scaler?.version
        ? `${config.firmware.android.version} + ${config.firmware.scaler.version}`
        : config.firmware?.android?.version) || t('firmwarenotavailable');

  return (
    <Box>
      <Grid templateColumns="repeat(6, 1fr)" gap={2} mb={10}>
        <Stat py={4} px={0} rounded="md">
          <HStack spacing={3} justify="center">
            <DisplayStatsIcon boxSize={14} />
            <VStack spacing={0} align="start">
              <StatNumber>{config.configStatistics?.assignedDisplays}</StatNumber>
              <StatLabel fontWeight="normal" fontSize="md">
                {t('assignedDisplays')}
              </StatLabel>
            </VStack>
          </HStack>
        </Stat>
        <Stat py={4} px={0} rounded="md">
          <HStack spacing={3} justify="center">
            <StatsSyncIcon boxSize={14} mb={2} />
            <VStack spacing={0} align="start">
              <StatNumber>{config.configStatistics?.inSyncDisplays}</StatNumber>
              <StatLabel fontWeight="normal" fontSize="md">
                {t('inSync')}
              </StatLabel>
            </VStack>
          </HStack>
        </Stat>
        <Stat py={4} px={0} rounded="md">
          <HStack spacing={3} justify="center">
            <StatsSyncingIcon boxSize={14} />
            <VStack spacing={0} align="start">
              <StatNumber>{config.configStatistics?.syncingDisplays}</StatNumber>
              <StatLabel fontWeight="normal" fontSize="md">
                {t('Syncing')}
              </StatLabel>
            </VStack>
          </HStack>
        </Stat>
        <Stat py={4} px={0} rounded="md">
          <HStack spacing={3} justify="center">
            <StatsWarningIcon boxSize={14} />
            <VStack spacing={0} align="start">
              <StatNumber>{config.configStatistics?.outOfSyncDisplays}</StatNumber>
              <StatLabel fontWeight="normal" fontSize="md">
                {t('OutOfSync')}
              </StatLabel>
            </VStack>
          </HStack>
        </Stat>
        <Stat py={4} px={0} rounded="md">
          <HStack spacing={3} justify="center">
            <StatsFailedIcon boxSize={14} />
            <VStack spacing={0} align="start">
              {/* <StatNumber>{config.configStatistics?.failedToSyncDisplays}</StatNumber> */}
              <StatLabel fontWeight="normal" fontSize="md">
                {t('failedToSync')}
              </StatLabel>
            </VStack>
          </HStack>
        </Stat>
      </Grid>
      <Grid templateColumns="repeat(4, 1fr)" gap={8}>
        <VStack align="start" spacing={1}>
          <Text fontWeight="bold">{t('configurationName')}</Text>
          <Text>{config.name}</Text>
        </VStack>
        <VStack align="start" spacing={1}>
          <Text fontWeight="bold">{t('description')}</Text>
          <Text>{config.description}</Text>
        </VStack>
        <VStack align="start" spacing={1}>
          <Text fontWeight="bold">{t('createdOn')}</Text>
          <Text>{format(new Date(config.createdAt), 'do MMMM yyyy')}</Text>
        </VStack>
        <VStack align="start" spacing={1} minH="2rem">
          {config.publisherUserName?.trim() ? (
            <>
              <Text fontWeight="bold">{t('publishedBy')}</Text>
              <Text>{config.publisherUserName}</Text>
            </>
          ) : (
            <>
              <Text>&nbsp;</Text>
              <Text>&nbsp;</Text>
            </>
          )}
        </VStack>
        <VStack align="start" spacing={1}>
          <Text fontWeight="bold">{t('firmware')}</Text>
          <Text>{firmware}</Text>
        </VStack>

        <VStack align="start" spacing={1}>
          <Text fontWeight="bold" px={1} rounded="sm">
            {t('displayModel')}
          </Text>
          <Text>{config.platform}</Text>
        </VStack>
        {showShouldLastPublished && (
          <VStack align="start" spacing={1}>
            <Text fontWeight="bold">{t('lastPublished')}</Text>
            <Text>
              {config.updatedAt ? format(new Date(config.updatedAt), 'do MMMM yyyy') : ''}
            </Text>
          </VStack>
        )}
      </Grid>
    </Box>
  );
}

export function ConfigSummary() {
  const { configuration } = useConfigDetailSettingsTab();
  return <ConfigSummaryView config={configuration} />;
}

ConfigSummary.graphql = {
  fragments: {
    ConfigSummary_config: gql`
      fragment ConfigSummary_config on ConfigurationModel {
        id
        name
        firmware {
          android {
            version
          }
          scaler {
            version
          }
        }
        shouldUpdateToLatestFirmware
        createdAt
        platform
        description
        updatedAt
        publisherUserName
        configStatistics {
          assignedDisplays
          inSyncDisplays
          outOfSyncDisplays
          syncingDisplays
        }
      }
    `,
  },
};
